/**
 * Silently warm the React Query cache for the next few weeks of
 * interviewer working hours. Pairs with `useInterviewerScheduleQuery`
 * so that when the calendar advances, the visible-week fetch is a
 * cache hit instead of a fresh network round-trip.
 *
 * Mirrors the spirit (but not the size) of the `calendarEvents`
 * chunks + lookahead prefetch: one `useQuery` per week × user-id set.
 */
import { useQueries, type UseQueryOptions } from '@tanstack/react-query';
import moment from 'moment-timezone';
import { normalizeTimeZoneForAtsCalendarApi } from '@/api/calendarEvents';
import {
  fetchInterviewerSchedule,
  type FetchInterviewerScheduleArgs,
} from './fetchInterviewerSchedule';
import { buildInterviewerScheduleQueryKey } from './useInterviewerScheduleQuery';
import type { InterviewerScheduleByUserId } from './types';

export interface UseInterviewerSchedulePrefetchArgs {
  /** Anchor (visible) week start. We skip this one (the live query already owns it). */
  anchorWeekStartIso: string;
  timezone: string;
  interviewerIds: number[];
  /** Total number of weeks to keep warm, including the anchor. */
  prefetchWeeks?: number;
  /** Extra quiet weeks to fetch after `prefetchWeeks`. */
  lookaheadPrefetchWeeks?: number;
  enabled?: boolean;
}

export function useInterviewerSchedulePrefetch({
  anchorWeekStartIso,
  timezone,
  interviewerIds,
  prefetchWeeks = 3,
  lookaheadPrefetchWeeks = 1,
  enabled = true,
}: UseInterviewerSchedulePrefetchArgs): void {
  const sortedIds = [...interviewerIds]
    .filter((id) => Number.isFinite(id) && id > 0)
    .sort((a, b) => a - b);

  const tz = normalizeTimeZoneForAtsCalendarApi(timezone);
  const totalWeeks = Math.max(0, prefetchWeeks + lookaheadPrefetchWeeks);

  // Week offsets 1..totalWeeks — offset 0 is served by the live
  // useInterviewerScheduleQuery and must not be re-requested here.
  const chunks: Array<{
    weekStartIso: string;
    args: FetchInterviewerScheduleArgs;
  }> = [];
  for (let offset = 1; offset < totalWeeks; offset += 1) {
    const weekStartIso = moment(anchorWeekStartIso)
      .tz(tz)
      .add(offset, 'weeks')
      .toISOString();
    chunks.push({
      weekStartIso,
      args: {
        weekStartIso,
        timezone,
        interviewerIds: sortedIds,
      },
    });
  }

  const isActive =
    Boolean(enabled) &&
    sortedIds.length > 0 &&
    Boolean(anchorWeekStartIso) &&
    timezone !== 'local' &&
    chunks.length > 0;

  const queries: UseQueryOptions<InterviewerScheduleByUserId, Error>[] =
    chunks.map((c) => ({
      queryKey: buildInterviewerScheduleQueryKey(
        c.weekStartIso,
        timezone,
        sortedIds
      ),
      queryFn: () => fetchInterviewerSchedule(c.args),
      enabled: isActive,
      staleTime: 60_000,
    }));

  useQueries({ queries });
}
